import { useState, useMemo, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import {
	UsersIcon,
	ClockIcon,
	MagnifyingGlassIcon,
	PlusIcon,
	PencilSquareIcon,
	EyeIcon,
	EnvelopeIcon,
	PhoneIcon,
	UserIcon,
	ExclamationTriangleIcon,
	ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import AppLayout from '../../components/layout/AppLayout';
import { Button, Badge, Modal, Drawer, Spinner } from '../../components/ui';
import useProviderProfileStore from '../../stores/providerProfileStore';
import staffApi from '../../api/staff';
import { appointmentsApi } from '../../api';
import type { StaffMember, StaffFormData } from '../../types/staff';
import type { ServiceTask } from '../../types/appointments';
import { getUniqueTasks, formatTaskType } from '../../utils/taskHelpers';

// ---- Constants ----
const AVATAR_COLORS = [
	'#667eea',
	'#764ba2',
	'#f59e0b',
	'#10b981',
	'#ec4899',
	'#3b82f6',
	'#8b5cf6',
	'#f97316',
	'#06b6d4',
	'#14b8a6',
	'#a855f7',
	'#84cc16',
];

const getAvatarColor = (id: number) => AVATAR_COLORS[id % AVATAR_COLORS.length];

const emptyFormData: StaffFormData = {
	firstName: '',
	lastName: '',
	email: '',
	phone: '',
	role: 'BRAIDER',
	status: 'ACTIVE',
	staffType: 'PERMANENT',
	staffGrade: 'ENTRY',
	hireDate: new Date().toISOString().split('T')[0],
	commissionRate: 0,
	selectedTaskIds: [],
	locationIds: [],
};

const staffMemberToFormData = (s: StaffMember): StaffFormData => ({
	firstName: s.firstName,
	lastName: s.lastName,
	email: s.email,
	phone: s.phone,
	role: s.role,
	status: s.status,
	staffType: s.staffType,
	staffGrade: s.staffGrade,
	hireDate: s.hireDate,
	commissionRate: s.commissionRate,
	selectedTaskIds: s.selectedTaskIds ?? [],
	locationIds: s.locationIds ?? [],
});

// ---- Helpers ----
const getInitials = (first: string, last: string) =>
	`${first.charAt(0)}${last.charAt(0)}`.toUpperCase();

const getStatusBadge = (staff: StaffMember) => {
	switch (staff.status) {
		case 'ACTIVE':
			return <Badge variant="success">{staff.statusName}</Badge>;
		case 'ON_LEAVE':
			return <Badge variant="warning">{staff.statusName}</Badge>;
		case 'INACTIVE':
			return <Badge variant="danger">{staff.statusName}</Badge>;
		case 'TERMINATED':
			return <Badge variant="danger">{staff.statusName}</Badge>;
		default:
			return <Badge variant="info">{staff.statusName}</Badge>;
	}
};

const getRoleBadge = (staff: StaffMember) => {
	switch (staff.role) {
		case 'BRAIDER':
			return <Badge variant="primary">{staff.roleName}</Badge>;
		case 'STYLIST':
			return <Badge variant="info">{staff.roleName}</Badge>;
		case 'MANAGER':
			return <Badge variant="warning">{staff.roleName}</Badge>;
		case 'RECEPTIONIST':
			return <Badge variant="danger">{staff.roleName}</Badge>;
		default:
			return <Badge variant="info">{staff.roleName}</Badge>;
	}
};

// ---- Sub-components ----

// Stats Grid
const StatsGrid = ({ staff }: { staff: StaffMember[] }) => {
	const totalStaff = staff.length;
	const activeStaff = staff.filter(s => s.status === 'ACTIVE').length;
	const stylistsBraiders = staff.filter(s => s.role === 'STYLIST' || s.role === 'BRAIDER').length;
	const onLeave = staff.filter(s => s.status === 'ON_LEAVE').length;

	const stats = [
		{
			label: 'Total Staff',
			value: totalStaff,
			icon: UsersIcon,
			colorClass: 'staff-stat-card--purple',
		},
		{
			label: 'Active',
			value: activeStaff,
			icon: CheckCircleIcon,
			colorClass: 'staff-stat-card--green',
		},
		{
			label: 'Stylists / Braiders',
			value: stylistsBraiders,
			icon: UserIcon,
			colorClass: 'staff-stat-card--blue',
		},
		{
			label: 'On Leave',
			value: onLeave,
			icon: ClockIcon,
			colorClass: 'staff-stat-card--orange',
		},
	];

	return (
		<div className="staff-stats">
			{stats.map(stat => (
				<div key={stat.label} className={`staff-stat-card ${stat.colorClass}`}>
					<div className="staff-stat-card__icon">
						<stat.icon className="w-6 h-6" />
					</div>
					<div className="staff-stat-card__content">
						<span className="staff-stat-card__value">{stat.value}</span>
						<span className="staff-stat-card__label">{stat.label}</span>
					</div>
				</div>
			))}
		</div>
	);
};

// Staff Detail Drawer
interface StaffDetailDrawerProps {
	staff: StaffMember | null;
	isOpen: boolean;
	onClose: () => void;
	onEdit: (staff: StaffMember) => void;
}

const StaffDetailDrawer = ({ staff, isOpen, onClose, onEdit }: StaffDetailDrawerProps) => {
	return (
		<Drawer
			isOpen={isOpen}
			onClose={onClose}
			title="Staff Details"
			width="md"
			footer={
				staff && (
					<div className="flex gap-3">
						<Button
							variant="primary"
							className="flex-1"
							onClick={() => {
								onClose();
								onEdit(staff);
							}}
						>
							<PencilSquareIcon className="w-4 h-4" />
							Edit Staff
						</Button>
						<Button variant="secondary" className="flex-1" onClick={onClose}>
							Close
						</Button>
					</div>
				)
			}
		>
			{staff && (
				<div className="staff-drawer">
					{/* Avatar + Name */}
					<div className="staff-drawer__header">
						<div
							className="staff-drawer__avatar"
							style={{ background: getAvatarColor(staff.id) }}
						>
							{getInitials(staff.firstName, staff.lastName)}
						</div>
						<div className="staff-drawer__name-block">
							<h3 className="staff-drawer__name">
								{staff.firstName} {staff.lastName}
							</h3>
							<div className="flex items-center gap-2 mt-1">
								{getRoleBadge(staff)}
								{getStatusBadge(staff)}
							</div>
						</div>
					</div>

					{/* Contact Info */}
					<div className="staff-drawer__section">
						<h4 className="staff-drawer__section-title">Contact Information</h4>
						<div className="staff-drawer__detail-row">
							<EnvelopeIcon className="w-4 h-4 text-gray-400 shrink-0" />
							<span>{staff.email}</span>
						</div>
						<div className="staff-drawer__detail-row">
							<PhoneIcon className="w-4 h-4 text-gray-400 shrink-0" />
							<span>{staff.phone}</span>
						</div>
					</div>

					{/* Employment Details */}
					<div className="staff-drawer__section">
						<h4 className="staff-drawer__section-title">Employment Details</h4>
						<div className="staff-drawer__grid">
							<div className="staff-drawer__field">
								<span className="staff-drawer__field-label">Staff Type</span>
								<span className="staff-drawer__field-value">
									{staff.staffTypeName}
								</span>
							</div>
							<div className="staff-drawer__field">
								<span className="staff-drawer__field-label">Hire Date</span>
								<span className="staff-drawer__field-value">
									{new Date(staff.hireDate).toLocaleDateString('en-US', {
										month: 'short',
										day: 'numeric',
										year: 'numeric',
									})}
								</span>
							</div>
							<div className="staff-drawer__field">
								<span className="staff-drawer__field-label">Grade</span>
								<span className="staff-drawer__field-value">
									{staff.staffGradeName}
								</span>
							</div>
							<div className="staff-drawer__field">
								<span className="staff-drawer__field-label">Commission Rate</span>
								<span className="staff-drawer__field-value">
									{staff.commissionRate}%
								</span>
							</div>
						</div>
					</div>
				</div>
			)}
		</Drawer>
	);
};

// Add/Edit Staff Modal
interface AddEditStaffModalProps {
	isOpen: boolean;
	onClose: () => void;
	staff: StaffMember | null;
	onSave: (data: StaffFormData, id?: number) => void;
	isSaving: boolean;
	allServiceTasks: ServiceTask[];
	isLoadingServiceTasks: boolean;
	locationId: number | null;
	locations: {
		id: number;
		locationName: string | null;
		address: string;
		city: string;
		state: string;
		active: boolean;
	}[];
}

const AddEditStaffModal = ({
	isOpen,
	onClose,
	staff,
	onSave,
	isSaving,
	allServiceTasks,
	isLoadingServiceTasks,
	locationId,
	locations,
}: AddEditStaffModalProps) => {
	const isEditing = !!staff;
	const [formData, setFormData] = useState<StaffFormData>(emptyFormData);
	const [isTasksExpanded, setIsTasksExpanded] = useState(false);
	const [selectedTaskIds, setSelectedTaskIds] = useState<Set<number>>(new Set());
	const [isLoadingDetail, setIsLoadingDetail] = useState(false);

	const [selectedLocationIds, setSelectedLocationIds] = useState<Set<number>>(new Set());

	// Reset form when modal opens with new staff
	useState(() => {
		if (staff) {
			setFormData(staffMemberToFormData(staff));
			setSelectedTaskIds(new Set(staff.selectedTaskIds ?? []));
			setSelectedLocationIds(new Set(staff.locationIds ?? []));
		} else {
			setFormData({ ...emptyFormData });
			setSelectedTaskIds(new Set());
			// For new PERMANENT staff, default to active location; ON_CALL starts empty
			if (emptyFormData.staffType === 'PERMANENT' && locationId) {
				setSelectedLocationIds(new Set([locationId]));
			} else {
				setSelectedLocationIds(new Set());
			}
		}
		setIsTasksExpanded(false);
	});

	// Fetch staff detail to get current selectedTaskIds when editing
	useEffect(() => {
		if (isOpen && staff?.id && locationId) {
			setIsLoadingDetail(true);
			staffApi
				.getStaffDetail(staff.id, locationId)
				.then(detail => {
					setSelectedTaskIds(new Set(detail.selectedTaskIds ?? []));
					setSelectedLocationIds(new Set(detail.locationIds ?? []));
				})
				.catch(() => {
					// Fall back to what we have from list API
				})
				.finally(() => setIsLoadingDetail(false));
		}
	}, [isOpen, staff?.id, locationId]);

	const updateField = <K extends keyof StaffFormData>(key: K, value: StaffFormData[K]) => {
		setFormData(prev => ({ ...prev, [key]: value }));
	};

	/* Staff search handlers - hidden for now
	const handleSearchStaff = useCallback(async (query: string): Promise<StaffMember[]> => {
		return staffApi.searchStaff(query);
	}, []);

	const handleStaffSearchSelect = useCallback((selected: StaffMember) => {
		setFormData(staffMemberToFormData(selected));
		setSelectedTaskIds(new Set(selected.selectedTaskIds ?? []));
	}, []);

	const handleSearchClear = useCallback(() => {
		setFormData({ ...emptyFormData });
		setSelectedTaskIds(new Set());
	}, []);
	*/

	const toggleTask = useCallback((taskId: number) => {
		setSelectedTaskIds(prev => {
			const next = new Set(prev);
			if (next.has(taskId)) {
				next.delete(taskId);
			} else {
				next.add(taskId);
			}
			return next;
		});
	}, []);

	const toggleAllForService = useCallback((service: ServiceTask, select: boolean) => {
		const uniqueTasks = getUniqueTasks(service);
		setSelectedTaskIds(prev => {
			const next = new Set(prev);
			for (const t of uniqueTasks) {
				if (select) {
					next.add(t.serviceTaskId);
				} else {
					next.delete(t.serviceTaskId);
				}
			}
			return next;
		});
	}, []);

	const toggleAllServices = useCallback(
		(select: boolean) => {
			if (select) {
				const allIds = new Set<number>();
				for (const svc of allServiceTasks) {
					for (const t of getUniqueTasks(svc)) {
						allIds.add(t.serviceTaskId);
					}
				}
				setSelectedTaskIds(allIds);
			} else {
				setSelectedTaskIds(new Set());
			}
		},
		[allServiceTasks]
	);

	// Derive summary counts
	const taskSummary = useMemo(() => {
		let serviceCount = 0;
		let taskCount = selectedTaskIds.size;
		for (const svc of allServiceTasks) {
			const uniqueTasks = getUniqueTasks(svc);
			if (uniqueTasks.some(t => selectedTaskIds.has(t.serviceTaskId))) {
				serviceCount++;
			}
		}
		return { serviceCount, taskCount };
	}, [selectedTaskIds, allServiceTasks]);

	const handleSubmit = () => {
		if (
			!formData.firstName.trim() ||
			!formData.lastName.trim() ||
			!formData.email.trim() ||
			!formData.phone.trim()
		) {
			toast.error('Please fill in all required fields.');
			return;
		}
		if (selectedLocationIds.size === 0) {
			toast.error('Please select at least one location.');
			return;
		}
		const finalData: StaffFormData = {
			...formData,
			selectedTaskIds: Array.from(selectedTaskIds),
			locationIds: Array.from(selectedLocationIds),
		};
		onSave(finalData, staff?.id);
	};

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title={isEditing ? 'Edit Staff Member' : 'Add Staff Member'}
			size="xl"
			bodyClassName="max-h-[70vh] overflow-y-auto"
		>
			<div className="space-y-5">
				{/* Staff Search - Add mode only (hidden for now) */}
				{/* {!isEditing && (
					<div className="pb-4 mb-1 border-b border-gray-200 dark:border-gray-700">
						<Autocomplete<StaffMember>
							label="Search Staff by Phone or Email"
							helperText="Search for an existing staff member to pre-fill the form"
							placeholder="Enter name, phone, or email to search..."
							onSearch={handleSearchStaff}
							onSelect={handleStaffSearchSelect}
							onClear={handleSearchClear}
							renderItem={s => (
								<div className="flex items-center gap-3">
									<div
										className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0"
										style={{ background: getAvatarColor(s.id) }}
									>
										{getInitials(s.firstName, s.lastName)}
									</div>
									<div className="flex-1 min-w-0">
										<div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
											{s.firstName} {s.lastName}
										</div>
										<div className="text-xs text-gray-500 dark:text-gray-400 truncate">
											{s.email} &middot; {s.phone}
										</div>
									</div>
								</div>
							)}
							getItemKey={s => s.id}
							minChars={3}
						/>
					</div>
				)} */}

				{/* Name Row */}
				<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
					<div className="provider-form__group mb-0">
						<label className="provider-form__label">
							First Name <span className="provider-form__required">*</span>
						</label>
						<input
							type="text"
							className="provider-form__input"
							value={formData.firstName}
							onChange={e => updateField('firstName', e.target.value)}
							placeholder="First name"
						/>
					</div>
					<div className="provider-form__group mb-0">
						<label className="provider-form__label">
							Last Name <span className="provider-form__required">*</span>
						</label>
						<input
							type="text"
							className="provider-form__input"
							value={formData.lastName}
							onChange={e => updateField('lastName', e.target.value)}
							placeholder="Last name"
						/>
					</div>
				</div>

				{/* Contact Row */}
				<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
					<div className="provider-form__group mb-0">
						<label className="provider-form__label">
							Email <span className="provider-form__required">*</span>
						</label>
						<input
							type="email"
							className="provider-form__input"
							value={formData.email}
							onChange={e => updateField('email', e.target.value)}
							placeholder="email@example.com"
						/>
					</div>
					<div className="provider-form__group mb-0">
						<label className="provider-form__label">
							Phone <span className="provider-form__required">*</span>
						</label>
						<input
							type="tel"
							className="provider-form__input"
							value={formData.phone}
							onChange={e => updateField('phone', e.target.value)}
							placeholder="(555) 123-4567"
						/>
					</div>
				</div>

				{/* Role + Status */}
				<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
					<div className="provider-form__group mb-0">
						<label className="provider-form__label">
							Role <span className="provider-form__required">*</span>
						</label>
						<select
							className="provider-form__input"
							value={formData.role}
							onChange={e => updateField('role', e.target.value)}
						>
							<option value="BRAIDER">Braider</option>
							<option value="STYLIST">Stylist</option>
							<option value="MANAGER">Manager</option>
							<option value="RECEPTIONIST">Receptionist</option>
						</select>
					</div>
					<div className="provider-form__group mb-0">
						<label className="provider-form__label">Status</label>
						<select
							className="provider-form__input"
							value={formData.status}
							onChange={e => updateField('status', e.target.value)}
						>
							<option value="ACTIVE">Active</option>
							<option value="ON_LEAVE">On Leave</option>
							<option value="INACTIVE">Inactive</option>
							<option value="TERMINATED">Terminated</option>
						</select>
					</div>
				</div>

				{/* Staff Type + Hire Date */}
				<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
					<div className="provider-form__group mb-0">
						<label className="provider-form__label">Staff Type</label>
						<select
							className="provider-form__input"
							value={formData.staffType}
							onChange={e => {
								const newType = e.target.value;
								updateField('staffType', newType);
								if (newType === 'PERMANENT' && locationId) {
									setSelectedLocationIds(new Set([locationId]));
								}
							}}
						>
							<option value="PERMANENT">Permanent</option>
							<option value="ON_CALL">On Call</option>
						</select>
					</div>
					<div className="provider-form__group mb-0">
						<label className="provider-form__label">Hire Date</label>
						<input
							type="date"
							className="provider-form__input"
							value={formData.hireDate}
							onChange={e => updateField('hireDate', e.target.value)}
						/>
					</div>
				</div>

				{/* Location Assignment */}
				{locations.length > 0 && (
					<div>
						<label className="provider-form__label">
							Locations{' '}
							{formData.staffType === 'ON_CALL' && (
								<span className="text-red-500">*</span>
							)}
						</label>
						<p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
							{formData.staffType === 'PERMANENT'
								? 'Permanent staff are assigned to the current active location.'
								: 'Select one or more locations for this on-call staff member.'}
						</p>
						<div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
							{(formData.staffType === 'PERMANENT'
								? locations.filter(l => l.id === locationId)
								: locations
							).map(loc => {
								const isChecked = selectedLocationIds.has(loc.id);
								const isPermanent = formData.staffType === 'PERMANENT';
								return (
									<label
										key={loc.id}
										className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
											isChecked
												? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 dark:border-purple-400'
												: 'border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50'
										} ${isPermanent ? 'opacity-80 cursor-default' : 'hover:border-purple-300 dark:hover:border-purple-600'}`}
									>
										<input
											type="checkbox"
											checked={isChecked}
											disabled={isPermanent}
											onChange={() => {
												if (isPermanent) return;
												setSelectedLocationIds(prev => {
													const next = new Set(prev);
													if (next.has(loc.id)) {
														next.delete(loc.id);
													} else {
														next.add(loc.id);
													}
													return next;
												});
											}}
											className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
										/>
										<div className="flex-1 min-w-0">
											<p className="text-sm font-medium text-gray-900 dark:text-white truncate">
												{loc.locationName ||
													[loc.address, loc.city, loc.state]
														.filter(Boolean)
														.join(', ')}
											</p>
											<p className="text-xs text-gray-500 dark:text-gray-400">
												{loc.active ? 'Active' : 'Inactive'}
											</p>
										</div>
									</label>
								);
							})}
						</div>
					</div>
				)}

				{/* Grade + Commission */}
				<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
					<div className="provider-form__group mb-0">
						<label className="provider-form__label">Grade</label>
						<select
							className="provider-form__input"
							value={formData.staffGrade}
							onChange={e => updateField('staffGrade', e.target.value)}
						>
							<option value="ENTRY">Entry</option>
							<option value="EXPERT">Expert</option>
							<option value="PRO">Pro</option>
							<option value="MASTER">Master</option>
						</select>
					</div>
					<div className="provider-form__group mb-0">
						<label className="provider-form__label">Commission Rate (%)</label>
						<input
							type="number"
							className="provider-form__input"
							min={0}
							max={100}
							value={formData.commissionRate}
							onChange={e => updateField('commissionRate', Number(e.target.value))}
						/>
					</div>
				</div>

				{/* Services & Tasks Accordion */}
				<div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
					<button
						type="button"
						className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer"
						onClick={() => setIsTasksExpanded(!isTasksExpanded)}
					>
						<div className="flex items-center gap-2">
							<span className="text-sm font-semibold text-gray-700 dark:text-gray-200">
								Services & Tasks
							</span>
							<span className="text-xs text-gray-500 dark:text-gray-400">
								{taskSummary.taskCount > 0
									? `${taskSummary.serviceCount} service${taskSummary.serviceCount !== 1 ? 's' : ''}, ${taskSummary.taskCount} task${taskSummary.taskCount !== 1 ? 's' : ''}`
									: 'No tasks assigned'}
							</span>
						</div>
						<ChevronDownIcon
							className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isTasksExpanded ? 'rotate-180' : ''}`}
						/>
					</button>

					{isTasksExpanded && (
						<div className="px-4 py-3 max-h-[280px] overflow-y-auto border-t border-gray-200 dark:border-gray-700">
							{isLoadingServiceTasks || isLoadingDetail ? (
								<div className="py-6 text-center">
									<Spinner size="sm" className="mx-auto mb-2 text-purple-600" />
									<p className="text-xs text-gray-500 dark:text-gray-400">
										Loading services & tasks...
									</p>
								</div>
							) : allServiceTasks.length === 0 ? (
								<p className="py-4 text-center text-sm text-gray-400 dark:text-gray-500">
									No services available for this location.
								</p>
							) : (
								<div className="space-y-3">
									{(() => {
										const allTaskIds: number[] = [];
										for (const svc of allServiceTasks) {
											for (const t of getUniqueTasks(svc)) {
												allTaskIds.push(t.serviceTaskId);
											}
										}
										const allSelectedGlobal =
											allTaskIds.length > 0 &&
											allTaskIds.every(id => selectedTaskIds.has(id));
										return (
											<div className="flex items-center justify-between pb-2 border-b border-gray-200 dark:border-gray-700">
												<span className="text-xs font-medium text-gray-500 dark:text-gray-400">
													{selectedTaskIds.size} of {allTaskIds.length}{' '}
													tasks selected
												</span>
												<button
													type="button"
													className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:underline cursor-pointer"
													onClick={() =>
														toggleAllServices(!allSelectedGlobal)
													}
												>
													{allSelectedGlobal
														? 'Deselect All'
														: 'Select All'}
												</button>
											</div>
										);
									})()}
									{allServiceTasks.map(service => {
										const uniqueTasks = getUniqueTasks(service);
										const allSelected = uniqueTasks.every(t =>
											selectedTaskIds.has(t.serviceTaskId)
										);
										const someSelected = uniqueTasks.some(t =>
											selectedTaskIds.has(t.serviceTaskId)
										);

										return (
											<div
												key={service.id}
												className="border border-gray-100 dark:border-gray-700 rounded-lg p-3"
											>
												<div className="flex items-center justify-between mb-2">
													<h5 className="text-sm font-medium text-gray-800 dark:text-gray-200">
														{service.serviceName}
													</h5>
													<button
														type="button"
														className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline cursor-pointer"
														onClick={() =>
															toggleAllForService(
																service,
																!allSelected
															)
														}
													>
														{allSelected
															? 'Deselect All'
															: someSelected
																? 'Select All'
																: 'Select All'}
													</button>
												</div>
												<div className="flex flex-wrap gap-1.5">
													{uniqueTasks.map(task => {
														const isSelected = selectedTaskIds.has(
															task.serviceTaskId
														);
														return (
															<button
																key={task.serviceTaskId}
																type="button"
																className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-all cursor-pointer ${
																	isSelected
																		? 'bg-gradient-to-r from-[#667eea] to-[#764ba2] text-white shadow-sm'
																		: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
																}`}
																onClick={() =>
																	toggleTask(task.serviceTaskId)
																}
															>
																{isSelected && (
																	<CheckCircleIcon className="w-3 h-3" />
																)}
																{formatTaskType(task.taskType)}
															</button>
														);
													})}
												</div>
											</div>
										);
									})}
								</div>
							)}
						</div>
					)}
				</div>

				{/* Actions */}
				<div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
					<Button
						variant="secondary"
						className="flex-1"
						onClick={onClose}
						disabled={isSaving}
					>
						Cancel
					</Button>
					<Button
						variant="primary"
						className="flex-1"
						onClick={handleSubmit}
						disabled={isSaving}
					>
						{isSaving ? 'Saving...' : isEditing ? 'Save Changes' : 'Add Staff Member'}
					</Button>
				</div>
			</div>
		</Modal>
	);
};

// ---- Main StaffManagement Page ----
const StaffManagement = () => {
	const { selectedLocationId, profile } = useProviderProfileStore();
	const [staffList, setStaffList] = useState<StaffMember[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [isSaving, setIsSaving] = useState(false);
	const [searchQuery, setSearchQuery] = useState('');
	const [roleFilter, setRoleFilter] = useState('');
	const [statusFilter, setStatusFilter] = useState('');
	const [selectedStaff, setSelectedStaff] = useState<StaffMember | null>(null);
	const [isDrawerOpen, setIsDrawerOpen] = useState(false);
	const [editingStaff, setEditingStaff] = useState<StaffMember | null>(null);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [allServiceTasks, setAllServiceTasks] = useState<ServiceTask[]>([]);
	const [isLoadingServiceTasks, setIsLoadingServiceTasks] = useState(false);

	// Fetch service tasks for location
	const fetchServiceTasks = useCallback(async (locationId: number) => {
		setIsLoadingServiceTasks(true);
		try {
			const data = await appointmentsApi.getServiceTasks(locationId);
			setAllServiceTasks(data);
		} catch {
			setAllServiceTasks([]);
		} finally {
			setIsLoadingServiceTasks(false);
		}
	}, []);

	// Fetch staff list
	const fetchStaffList = useCallback(async (locationId: number) => {
		setIsLoading(true);
		setError(null);
		try {
			const data = await staffApi.listStaff(locationId);
			setStaffList(data);
		} catch (err) {
			const message = err instanceof Error ? err.message : 'Failed to load staff';
			setError(message);
			setStaffList([]);
		} finally {
			setIsLoading(false);
		}
	}, []);

	// Watch selectedLocationId and refetch
	useEffect(() => {
		if (selectedLocationId) {
			fetchStaffList(selectedLocationId);
			fetchServiceTasks(selectedLocationId);
		}
	}, [selectedLocationId, fetchStaffList, fetchServiceTasks]);

	// Filtered staff (local filtering)
	const filteredStaff = useMemo(() => {
		return staffList.filter(staff => {
			const matchesSearch =
				!searchQuery ||
				`${staff.firstName} ${staff.lastName}`
					.toLowerCase()
					.includes(searchQuery.toLowerCase()) ||
				staff.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
				staff.phone.includes(searchQuery);

			const matchesRole = !roleFilter || staff.role === roleFilter;
			const matchesStatus = !statusFilter || staff.status === statusFilter;

			return matchesSearch && matchesRole && matchesStatus;
		});
	}, [staffList, searchQuery, roleFilter, statusFilter]);

	const handleViewStaff = (staff: StaffMember) => {
		setSelectedStaff(staff);
		setIsDrawerOpen(true);
	};

	const handleEditStaff = (staff: StaffMember) => {
		setEditingStaff(staff);
		setIsModalOpen(true);
	};

	const handleAddStaff = () => {
		setEditingStaff(null);
		setIsModalOpen(true);
	};

	const handleSaveStaff = async (data: StaffFormData, id?: number) => {
		if (!selectedLocationId) return;
		setIsSaving(true);
		try {
			if (id) {
				await staffApi.updateStaff(id, selectedLocationId, data);
				toast.success('Staff member updated successfully!');
			} else {
				await staffApi.createStaff(selectedLocationId, data);
				toast.success('Staff member added successfully!');
			}
			setIsModalOpen(false);
			setEditingStaff(null);
			fetchStaffList(selectedLocationId);
		} catch (err) {
			const message = err instanceof Error ? err.message : 'Failed to save staff member';
			toast.error(message);
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<AppLayout>
			{/* Page Header */}
			<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
				<div>
					<div className="flex items-center gap-3 mb-1">
						<UsersIcon className="w-6 h-6 text-gray-400" />
						<h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100">
							Staff Management
						</h1>
					</div>
					<p className="text-sm text-gray-500 dark:text-gray-400">
						Manage your team members, roles, and assignments
					</p>
				</div>
				<Button variant="primary" onClick={handleAddStaff}>
					<PlusIcon className="w-5 h-5" />
					Add Staff Member
				</Button>
			</div>

			{/* Loading State */}
			{isLoading && (
				<div className="py-16 text-center">
					<Spinner size="lg" className="mx-auto mb-3 text-purple-600" />
					<p className="text-sm text-gray-500 dark:text-gray-400">Loading staff...</p>
				</div>
			)}

			{/* Error State */}
			{!isLoading && error && (
				<div className="py-16 text-center">
					<ExclamationTriangleIcon className="w-16 h-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
					<h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
						Failed to load staff
					</h3>
					<p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{error}</p>
					{selectedLocationId && (
						<Button
							variant="primary"
							onClick={() => fetchStaffList(selectedLocationId)}
						>
							Try Again
						</Button>
					)}
				</div>
			)}

			{/* Content */}
			{!isLoading && !error && (
				<>
					{/* Stats Grid */}
					<StatsGrid staff={filteredStaff} />

					{/* Filter Bar */}
					<div className="settings-card mb-6">
						<div className="settings-card__body">
							<div className="staff-filters">
								<div className="staff-filters__selects">
									<select
										className="provider-form__input"
										value={roleFilter}
										onChange={e => setRoleFilter(e.target.value)}
									>
										<option value="">All Roles</option>
										<option value="BRAIDER">Braider</option>
										<option value="STYLIST">Stylist</option>
										<option value="MANAGER">Manager</option>
										<option value="RECEPTIONIST">Receptionist</option>
									</select>
									<select
										className="provider-form__input"
										value={statusFilter}
										onChange={e => setStatusFilter(e.target.value)}
									>
										<option value="">All Status</option>
										<option value="ACTIVE">Active</option>
										<option value="ON_LEAVE">On Leave</option>
										<option value="INACTIVE">Inactive</option>
										<option value="TERMINATED">Terminated</option>
									</select>
								</div>
								<div className="staff-filters__search">
									<MagnifyingGlassIcon className="staff-filters__search-icon" />
									<input
										type="text"
										className="provider-form__input staff-filters__search-input"
										placeholder="Search staff..."
										value={searchQuery}
										onChange={e => setSearchQuery(e.target.value)}
									/>
								</div>
							</div>
						</div>
					</div>

					{/* Staff Table (Desktop) */}
					<div className="staff-table-wrapper hidden md:block">
						<div className="settings-card">
							<div className="overflow-x-auto">
								<table className="staff-table">
									<thead>
										<tr>
											<th>Staff Member</th>
											<th>Status</th>
											<th>Grade</th>
											<th>Type</th>
											<th className="text-right">Actions</th>
										</tr>
									</thead>
									<tbody>
										{filteredStaff.length === 0 ? (
											<tr>
												<td
													colSpan={5}
													className="text-center py-10 text-gray-400"
												>
													No staff members found matching your filters.
												</td>
											</tr>
										) : (
											filteredStaff.map(staff => (
												<tr
													key={staff.id}
													className="staff-table__row"
													onClick={() => handleViewStaff(staff)}
												>
													<td>
														<div className="flex items-center gap-3">
															<div
																className="staff-table__avatar"
																style={{
																	background: getAvatarColor(
																		staff.id
																	),
																}}
															>
																{getInitials(
																	staff.firstName,
																	staff.lastName
																)}
															</div>
															<div>
																<div className="font-medium text-gray-900 dark:text-gray-100">
																	{staff.firstName}{' '}
																	{staff.lastName}
																</div>
																<div className="text-xs text-gray-500 dark:text-gray-400">
																	{staff.roleName} ·{' '}
																	{staff.staffGradeName}
																</div>
															</div>
														</div>
													</td>
													<td>{getStatusBadge(staff)}</td>
													<td>
														<span className="text-sm text-gray-700 dark:text-gray-300">
															{staff.staffGradeName}
														</span>
													</td>
													<td>
														<span className="text-sm text-gray-700 dark:text-gray-300">
															{staff.staffTypeName}
														</span>
													</td>
													<td className="text-right">
														<div className="flex items-center justify-end gap-1">
															<button
																type="button"
																className="p-2 rounded-lg text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 dark:hover:text-indigo-400 transition-all cursor-pointer"
																onClick={e => {
																	e.stopPropagation();
																	handleViewStaff(staff);
																}}
																title="View details"
															>
																<EyeIcon className="w-4 h-4" />
															</button>
															<button
																type="button"
																className="p-2 rounded-lg text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 dark:hover:text-indigo-400 transition-all cursor-pointer"
																onClick={e => {
																	e.stopPropagation();
																	handleEditStaff(staff);
																}}
																title="Edit"
															>
																<PencilSquareIcon className="w-4 h-4" />
															</button>
														</div>
													</td>
												</tr>
											))
										)}
									</tbody>
								</table>
							</div>
						</div>
					</div>

					{/* Staff Cards (Mobile) */}
					<div className="staff-cards md:hidden">
						{filteredStaff.length === 0 ? (
							<div className="settings-card">
								<div className="settings-card__body text-center py-10 text-gray-400">
									No staff members found matching your filters.
								</div>
							</div>
						) : (
							filteredStaff.map(staff => (
								<div
									key={staff.id}
									className="staff-card"
									onClick={() => handleViewStaff(staff)}
								>
									<div className="staff-card__header">
										<div
											className="staff-card__avatar"
											style={{ background: getAvatarColor(staff.id) }}
										>
											{getInitials(staff.firstName, staff.lastName)}
										</div>
										<div className="staff-card__info">
											<h4 className="staff-card__name">
												{staff.firstName} {staff.lastName}
											</h4>
											<div className="flex items-center gap-2">
												{getRoleBadge(staff)}
											</div>
										</div>
										{getStatusBadge(staff)}
									</div>
									<div className="staff-card__body">
										<div className="staff-card__field">
											<span className="staff-card__field-label">Grade</span>
											<span className="staff-card__field-value">
												{staff.staffGradeName}
											</span>
										</div>
										<div className="staff-card__field">
											<span className="staff-card__field-label">Type</span>
											<span className="staff-card__field-value">
												{staff.staffTypeName}
											</span>
										</div>
									</div>
								</div>
							))
						)}
					</div>
				</>
			)}

			{/* Staff Detail Drawer */}
			<StaffDetailDrawer
				staff={selectedStaff}
				isOpen={isDrawerOpen}
				onClose={() => setIsDrawerOpen(false)}
				onEdit={handleEditStaff}
			/>

			{/* Add/Edit Modal */}
			<AddEditStaffModal
				key={editingStaff?.id ?? 'add'}
				isOpen={isModalOpen}
				onClose={() => {
					setIsModalOpen(false);
					setEditingStaff(null);
				}}
				staff={editingStaff}
				onSave={handleSaveStaff}
				isSaving={isSaving}
				allServiceTasks={allServiceTasks}
				isLoadingServiceTasks={isLoadingServiceTasks}
				locationId={selectedLocationId}
				locations={
					profile?.locations?.map(l => ({
						id: l.id,
						locationName: l.locationName,
						address: l.address,
						city: l.city,
						state: l.state,
						active: l.active,
					})) ?? []
				}
			/>
		</AppLayout>
	);
};

export default StaffManagement;
